import { useCallback, useEffect, useRef, useState } from 'react'

import { DEFAULT_RETRY_COUNTDOWN_SECONDS } from '../constants'

export interface RetryCountdownState {
  countdown: number
  isCountingDown: boolean
  startCountdown: (seconds?: number) => void
  stopCountdown: () => void
}

export interface UseRetryCountdownOptions {
  defaultSeconds?: number
  onCountdownComplete?: () => void
}

export const useRetryCountdown = ({
  defaultSeconds = DEFAULT_RETRY_COUNTDOWN_SECONDS,
  onCountdownComplete,
}: UseRetryCountdownOptions = {}): RetryCountdownState => {
  const [countdown, setCountdown] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const onCompleteRef = useRef(onCountdownComplete)

  useEffect(() => {
    onCompleteRef.current = onCountdownComplete
  }, [onCountdownComplete])

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  const isCountingDown = countdown > 0

  const stopCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setCountdown(0)
  }, [])

  const startCountdown = useCallback(
    (seconds: number = defaultSeconds) => {
      stopCountdown()

      setCountdown(seconds)

      intervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          const next = prev - 1
          if (next <= 0) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current)
              intervalRef.current = null
            }
            if (onCompleteRef.current) {
              setTimeout(() => onCompleteRef.current?.(), 0)
            }
            return 0
          }
          return next
        })
      }, 1000)
    },
    [defaultSeconds, stopCountdown],
  )

  return {
    countdown,
    isCountingDown,
    startCountdown,
    stopCountdown,
  }
}

export default useRetryCountdown
